import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Camera,
  CircleHelp,
  FileText,
  Hand,
  ImageUp,
  Mic,
  Scan,
  ScanBarcode,
  SendHorizontal,
  SunDim,
  SwitchCamera,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet } from '@/components/ui/sheet';
import {
  CameraLayout,
  CropGuide,
  ModeSwitch,
  OverlayButton,
  OverlayLink,
  ShutterButton,
  SideControl,
  type CameraModeChoice,
} from '@/components/capture/CameraLayout';
import { cn } from '@/lib/utils';
import { useCamera, type CameraZoom } from '@/lib/camera';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { CAPTURE_TIPS, useCaptureTips } from '@/hooks/useCaptureTips';
import { prefersCameraCapture } from '@/lib/platform';
import { looksLikeImage } from '@/lib/image-source';
import { captureFingerprint, sha256Fingerprint } from '@/lib/fingerprint';
import { downscaleForUpload } from '@/lib/downscale-image';
import { runQualityGate, sampleImage } from '@/lib/quality-gate';
import { adviseOnQuality, gradeLabel, type QualityAdvice, type QualityGrade } from '@/lib/quality-advice';
import { decodeBarcodeFromImage } from '@/lib/barcode';
import { setBusy as markBusy } from '@/lib/busy';
import {
  enqueueAndDeliver,
  enqueueTranscriptNote,
  processVoiceNoteQueue,
  removeQueuedCapture,
  type DeliveryState,
} from '@/lib/offline';
import {
  DEFAULT_SHOT_LIST,
  MAX_FRAMES,
  addFrame,
  claimFramesFor,
  clearFrames,
  holdForDecision,
  listFrames,
  removeFrame,
  shotSlots,
  type AddFrameOutcome,
  type CaptureFrame,
} from '@/lib/capture-frames';
import { getSessionId } from '@/lib/session';
import { readStoredWorkflow, workflowTitle } from '@/lib/capture-workflows';
import { getCaptureJob } from '@/lib/api/capture';
import { LiveTranscriptBox } from '@/components/voice/LiveTranscriptBox';
import { knownJobName, readCachedJobs, rememberCurrentJob } from '@/components/jobs/current-job';
import type { CaptureJob, CaptureMode, QualityGateResult } from '@/lib/api/types';

type SingleShotMode = Extract<CaptureMode, 'photo' | 'document'>;

/**
 * CA-03: Capture viewfinder, drawn to V-B in
 * plan/smart-capture/wireframes/mobile-fit.html. Process step 2. Opened from
 * a job (`/jobs/:jobId/capture`, CAPTURE_JOBS.md section 6), and every
 * capture sent from here carries that job's id. On shutter: sample pixels
 * for the on-device quality gate, compute the sha256 fingerprint BEFORE
 * anything touches the network (SC3 note 2), try an on-device barcode
 * decode, and add the frame to the capture being built.
 *
 * On a phone the preview runs edge to edge with no tab bar, the way the
 * phone's own camera app does, and Back returns to the job. The stored
 * image stays 3:4: the preview shows that region as a crop guide and the
 * shutter cuts exactly that region (`lib/camera/crop.ts`).
 *
 * One capture carries up to three frames, because an operator reads a part
 * number off one face of a pump and its rating plate off another, and the
 * extraction pipeline reads every frame it is sent. The job's shot list
 * (INTERFACES.md section 5) labels one slot per frame on the preview, filled
 * left to right; the slots guide and never block. The whole set goes to the
 * offline queue in one action (SC3 note 1), which is the only place this
 * screen sends anything.
 *
 * The gate result is always attached and never blocks the capture (SC3
 * note 3). A frame the gate wants to stop routes to CA-04 for the operator's
 * decision, and a frame kept there joins the set carrying `passed: false`,
 * which travels to the server with the rest.
 *
 * An image can also come off the device rather than the lens. A chosen file
 * runs the identical pipeline, because the fingerprint, the quality gate and
 * the barcode decode all read pixels and none of them cares where the pixels
 * came from. A touch device starts its camera on mount; a pointer device
 * starts nothing and leads with the file picker, because requesting
 * `getUserMedia` there only raises a permission prompt for a webcam aimed at
 * the person reviewing.
 *
 * Held frames and a held voice note are unsaved work a reload loses, so the
 * screen marks itself busy (`lib/busy.ts`) while either is held. The
 * deployment watcher reads that before reloading onto a new deployment.
 */

/**
 * Set on the navigation back from CA-04 when the operator kept a frame the
 * quality gate had failed, carrying what became of that frame, and on the
 * way back from rapid session, carrying the mode the operator picked there.
 */
interface ViewfinderEntryState {
  keptFromRetake?: AddFrameOutcome;
  mode?: SingleShotMode;
}

/** A live voice note (CAPTURE_JOBS.md section 3) saved before the capture
 *  is sent. It travels with the capture once the frames go. Held outside
 *  React for the same reason as the frames: CA-04 unmounts this screen. */
interface HeldNote {
  transcript: string;
  durationMs: number;
}
let heldNote: HeldNote | null = null;

/** Drops everything held for another job before this one starts. */
function claimHeldFor(jobId: string): void {
  if (claimFramesFor(jobId)) heldNote = null;
}

/** What `POST /capture/upload-url` accepts for a document-mode frame
 *  beside a photograph (INTERFACES.md 15.6): a single-page PDF. Multi page
 *  is refused server-side, with a message naming the limit. */
const DOCUMENT_CONTENT_TYPE = 'application/pdf';

/** Whether a chosen file is the PDF this screen's document mode accepts,
 *  by declared type or, where a platform reports none, by extension. */
function looksLikePdf(file: File): boolean {
  if (file.type === DOCUMENT_CONTENT_TYPE) return true;
  if (file.type !== '') return false;
  return /\.pdf$/i.test(file.name);
}

/** What the screen says about a frame offered to the capture. */
const OUTCOME_MESSAGE: Record<AddFrameOutcome, string | null> = {
  added: null,
  full: `Capture full at ${MAX_FRAMES} frames.`,
  duplicate: 'Same photo already held.',
};

const KEPT_MESSAGE: Record<AddFrameOutcome, string> = {
  added: 'Frame kept.',
  full: `Capture full at ${MAX_FRAMES} frames. Frame not kept.`,
  duplicate: 'Same photo already held.',
};

/** The refusal for a file the browser cannot decode (INTERFACES.md section 7). */
const UNREADABLE_IMAGE = 'Use JPEG or PNG.';

/**
 * What stands in the preview once a capture is queued: the lead frame, the
 * quality grade with its suggestions, and where the capture got to. The
 * preview is where the operator is already looking.
 */
interface CapturedShot {
  url: string;
  /** The lead frame's own type, so a PDF is not handed to an `<img>`. */
  contentType: string;
  advice: QualityAdvice | null;
  /** How many frames went up as this capture. */
  frameCount: number;
  delivery: DeliveryState | 'uploading';
  /** The `queue` row's own key, present as soon as the capture is sent. */
  localId: string;
  /** Set once the capture has arrived, which is what CA-05 is keyed on. */
  captureId?: string;
  /** The API's own words when it refused the capture (HTTP 402). */
  message?: string;
}

/**
 * The grade carries a colour only where it asks for something. A frame that
 * graded well with one check short is a note for the next shot.
 */
const GRADE_TONE: Record<QualityGrade, string> = {
  excellent: 'text-success',
  good: 'text-success',
  acceptable: 'text-warning',
  poor: 'text-danger',
};

function deliveryLine(shot: CapturedShot, online: boolean): string {
  const head = shot.frameCount > 1 ? `Queued ${shot.frameCount} frames.` : 'Queued.';
  if (shot.delivery === 'refused') return shot.message || 'Capture refused.';
  if (shot.delivery === 'uploading') return `${head} Uploading.`;
  if (shot.delivery === 'uploaded') return 'Uploaded. Reading.';
  return online ? `${head} Waiting to upload.` : `${head} Uploads when online.`;
}

/** Anything the gate was content with stays quiet on the slot. */
const GRADE_RING: Record<QualityGrade, string> = {
  excellent: 'border-accent',
  good: 'border-accent',
  acceptable: 'border-warning',
  poor: 'border-danger',
};

/** The zoom steps a tap on the zoom chip walks through, as a phone camera's 1x and 2x chips do. */
function nextZoom(zoom: CameraZoom): number {
  const steps = [zoom.min, 2, 5].filter((step, index, all) => step >= zoom.min && step <= zoom.max && all.indexOf(step) === index);
  return steps.find((step) => step > zoom.value + 0.05) ?? zoom.min;
}

/** The job's resolved shot list, from the last answer this device saw. */
function cachedJob(jobId: string): CaptureJob | null {
  return readCachedJobs()?.jobs.find((job) => job.jobId === jobId) ?? null;
}

const TIP_ICONS = [Scan, SunDim, Hand];

export function CaptureViewfinder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobId = '' } = useParams();
  const jobPath = `/jobs/${encodeURIComponent(jobId)}`;
  // Runs before the state below reads the held frames, so a set collected
  // for another job never shows here.
  claimHeldFor(jobId);
  const entry = location.state as ViewfinderEntryState | null;
  // Read once on mount: a device does not grow a touchscreen mid-capture.
  const [touchDevice] = useState(prefersCameraCapture);
  const [cameraRequested, setCameraRequested] = useState(touchDevice);
  const {
    videoRef,
    ready,
    error: cameraError,
    capturePhoto,
    canFlip,
    flip,
    zoom,
    setZoom,
  } = useCamera({ enabled: cameraRequested });
  const [job, setJob] = useState<CaptureJob | null>(() => cachedJob(jobId));
  const [jobName, setJobName] = useState<string | null>(() => knownJobName(jobId));
  const [note, setNote] = useState<HeldNote | null>(heldNote);
  const [mode, setMode] = useState<SingleShotMode>(entry?.mode ?? 'photo');
  const [barcodeEnabled, setBarcodeEnabled] = useState(true);
  const [working, setWorking] = useState(false);
  const [status, setStatus] = useState<string | null>(
    entry?.keptFromRetake ? KEPT_MESSAGE[entry.keptFromRetake] : null,
  );
  const [shot, setShot] = useState<CapturedShot | null>(null);
  const [dragging, setDragging] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const tips = useCaptureTips();
  // The frames collected so far. Held outside React (`lib/capture-frames`)
  // because CA-04 is a route and going there unmounts this screen, so this
  // is a view of that holder rather than the holder itself.
  const [frames, setFrames] = useState<CaptureFrame[]>(listFrames);
  // Read once per visit. Changing it goes through /capture/workflow, which
  // navigates back here and remounts.
  const [workflow] = useState(readStoredWorkflow);
  const online = useOnlineStatus();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const phoneCameraInputRef = useRef<HTMLInputElement>(null);

  // The job names the chip on the preview and carries the shot list. The
  // device's last copy serves at once and offline; a fresh read replaces it.
  useEffect(() => {
    let live = true;
    getCaptureJob(jobId)
      .then((fresh) => {
        if (!live) return;
        setJob(fresh);
        setJobName(fresh.name);
      })
      .catch(() => {
        // Offline or refused: the cached copy and the default list stand.
      });
    return () => {
      live = false;
    };
  }, [jobId]);

  useEffect(() => {
    if (jobName) rememberCurrentJob({ jobId, name: jobName });
  }, [jobId, jobName]);

  // Unsaved work: frames or a voice note held for this capture. No cleanup
  // on unmount, because the work outlives the screen until it is sent or
  // cleared.
  const holding = frames.length > 0 || note !== null;
  useEffect(() => {
    markBusy('viewfinder', holding);
  }, [holding]);

  function holdNote(next: HeldNote | null) {
    heldNote = next;
    setNote(next);
  }

  // One object URL for the queued capture's lead frame, released when the
  // next capture replaces it. Keyed on the URL string, so the delivery
  // transition that follows the upload does not revoke the image under it.
  useEffect(() => {
    const url = shot?.url;
    if (!url) return;
    return () => URL.revokeObjectURL(url);
  }, [shot?.url]);

  // A URL per thumbnail, rebuilt whenever the set changes. Three blobs is
  // the ceiling, so rebuilding the lot costs less than tracking which one
  // moved, and every URL this effect made is the one it revokes.
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  useEffect(() => {
    const urls: Record<string, string> = {};
    for (const frame of frames) {
      urls[frame.fingerprint] = URL.createObjectURL(frame.bytes);
    }
    setThumbnails(urls);
    return () => {
      for (const url of Object.values(urls)) URL.revokeObjectURL(url);
    };
  }, [frames]);

  // The first capture of a session names its purpose, which is the step the
  // proof of concept opened on. After that the choice is remembered and the
  // chip on the preview shows it back.
  useEffect(() => {
    if (!workflow && !entry?.keptFromRetake) {
      navigate(`${jobPath}/capture/workflow`, { replace: true });
    }
  }, [workflow, entry, navigate, jobPath]);

  // History state outlives a reload, so the confirmation is read once and
  // dropped.
  useEffect(() => {
    if (entry?.keptFromRetake || entry?.mode) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [entry, location.pathname, navigate]);

  const streaming = cameraRequested && !cameraError;
  const shotList = job?.shotList ?? DEFAULT_SHOT_LIST;
  const slots = shotSlots(shotList, frames);

  async function handleShutter() {
    setWorking(true);
    setStatus(null);
    setShot(null);
    try {
      const blob = await capturePhoto();
      if (!blob) {
        setStatus('No frame captured. Try again.');
        return;
      }
      await ingest(blob);
    } finally {
      setWorking(false);
    }
  }

  /** The shutter shoots the live stream; with none it opens the source that stands in for it. */
  function pressShutter() {
    if (streaming) {
      void handleShutter();
    } else if (touchDevice) {
      phoneCameraInputRef.current?.click();
    } else {
      fileInputRef.current?.click();
    }
  }

  const shutterLabel = streaming ? 'Shoot' : touchDevice ? 'Open camera' : 'Choose image';

  async function handleFileChosen(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Clear the input first, so choosing the same file twice in a row still
    // fires a change event.
    event.target.value = '';
    await acceptFile(file);
  }

  async function handleDrop(event: React.DragEvent) {
    event.preventDefault();
    setDragging(false);
    await acceptFile(event.dataTransfer.files?.[0]);
  }

  /** The one entry point for a file, picked or dropped. A PDF is accepted
   *  in document mode only, ahead of the image check below. */
  async function acceptFile(file: File | undefined) {
    if (!file) return;

    if (mode === 'document' && looksLikePdf(file)) {
      setWorking(true);
      setStatus(null);
      setShot(null);
      try {
        await ingestDocument(file);
      } finally {
        setWorking(false);
      }
      return;
    }

    if (!looksLikeImage(file)) {
      setStatus(mode === 'document' ? 'Use JPEG, PNG or PDF.' : UNREADABLE_IMAGE);
      return;
    }

    setWorking(true);
    setStatus(null);
    setShot(null);
    try {
      await ingest(file);
    } catch {
      // Whether this browser decodes the format is settled by trying it, not
      // by its extension: Safari reads HEIC and the other three engines do not.
      setStatus(UNREADABLE_IMAGE);
    } finally {
      setWorking(false);
    }
  }

  /**
   * A document-mode PDF (INTERFACES.md 15.6). No pixels to gate or decode
   * a barcode from, so this skips straight to a frame with a quality result
   * that trivially passes; the true validation (single page or not) is the
   * server's on `POST /capture/upload-url`.
   */
  async function ingestDocument(file: File) {
    const fingerprint = await sha256Fingerprint(file);
    const frame: CaptureFrame = {
      fingerprint,
      contentType: file.type || DOCUMENT_CONTENT_TYPE,
      bytes: file,
      qualityResult: { passed: true, checks: [] },
      advice: { grade: 'excellent', shouldStop: false, suggestions: [] },
      manualFields: {},
    };
    const outcome = addFrame(frame);
    if (outcome !== 'added') {
      setStatus(OUTCOME_MESSAGE[outcome]);
      return;
    }
    setFrames(listFrames());
  }

  /**
   * The shared path from pixels to the capture being built. The shutter and
   * the file picker both land here, so a chosen image is gated,
   * fingerprinted and decoded exactly as a shot one is.
   */
  async function ingest(original: Blob) {
    // Shrunk first, so the fingerprint, the gate and the queued bytes all
    // describe the image that is actually uploaded (src/lib/downscale-image.ts).
    const blob = await downscaleForUpload(original);
    // Fingerprint BEFORE any network call. A duplicate asset never
    // uploads its bytes (src/lib/api/capture.ts uploadCaptureAsset).
    const fingerprint = await sha256Fingerprint(blob);

    const bitmap = await createImageBitmap(blob);
    const sample = await sampleImage(bitmap);
    const qualityResult: QualityGateResult = runQualityGate(sample);

    const manualFields: Record<string, string> = {};
    if (barcodeEnabled) {
      const decoded = await decodeBarcodeFromImage(bitmap);
      if (decoded) {
        manualFields.barcodeValue = decoded.value;
        manualFields.barcodeSymbology = decoded.symbology;
      }
    }

    // The gate advises and the operator decides. A frame that failed most of
    // its checks and is soft or badly lit is the one case that still holds
    // the capture up, matching the proof of concept's stop.
    const advice = adviseOnQuality(qualityResult);
    const frame: CaptureFrame = {
      fingerprint,
      contentType: blob.type || 'image/jpeg',
      bytes: blob,
      qualityResult,
      advice,
      manualFields,
    };

    if (advice.shouldStop) {
      // The bytes travel in the module-scoped holder, never in navigation
      // state, which is structured-cloned into session history.
      holdForDecision(frame, mode);
      navigate(`${jobPath}/capture/retake`);
      return;
    }

    const outcome = addFrame(frame);
    if (outcome !== 'added') {
      setStatus(OUTCOME_MESSAGE[outcome]);
      return;
    }
    setFrames(listFrames());
  }

  /**
   * Sends the frames as one capture.
   *
   * The capture's own fingerprint is over the ordered frame fingerprints, so
   * the same angles resubmitted land on the same capture; each frame still
   * presigns on its own, which is what lets a frame the tenant already holds
   * skip its bytes.
   */
  async function queueFrames() {
    const held = listFrames();
    if (held.length === 0 || working) return;

    setWorking(true);
    setStatus(null);
    try {
      const lead = held[0];
      // The frame goes up in front of the operator before the upload starts,
      // so the screen answers the action at once.
      setShot({
        url: URL.createObjectURL(lead.bytes),
        contentType: lead.contentType,
        advice: lead.advice.suggestions.length > 0 ? lead.advice : null,
        frameCount: held.length,
        delivery: online ? 'uploading' : 'waiting',
        localId: '',
      });

      const fingerprint = await captureFingerprint(held.map((frame) => frame.fingerprint));
      // The submission carries one gate result and has no slot per asset, so
      // a capture holding a frame the gate failed reports that frame.
      const reportedQuality = held.find((frame) => !frame.qualityResult.passed) ?? lead;
      // Every frame's barcode read travels, because a barcode on the second
      // angle identifies the item just as well as one on the first.
      const manualFields = held.reduce<Record<string, string>>(
        (all, frame) => ({ ...all, ...frame.manualFields }),
        {},
      );

      const outcome = await enqueueAndDeliver({
        captureJobId: jobId,
        mode,
        ...(workflow ? { workflow } : {}),
        fingerprint,
        assets: held.map((frame) => ({
          fingerprint: frame.fingerprint,
          contentType: frame.contentType,
          bytes: frame.bytes,
        })),
        sessionId: getSessionId(),
        clientQuality: reportedQuality.qualityResult,
        ...(Object.keys(manualFields).length > 0 ? { manualFields } : {}),
      });

      // The voice note saved for this capture goes with it. It keys to the
      // queue row until the capture has a server id.
      const sentNote = heldNote;
      if (sentNote) {
        await enqueueTranscriptNote({
          ...(outcome.captureId ? { captureId: outcome.captureId } : { queuedCaptureLocalId: outcome.localId }),
          transcript: sentNote.transcript,
          durationMs: sentNote.durationMs,
          purpose: 'NOTE',
        });
        holdNote(null);
        if (outcome.captureId) void processVoiceNoteQueue();
      }

      clearFrames();
      setFrames([]);
      setShot((current) =>
        current
          ? {
              ...current,
              delivery: outcome.state,
              localId: outcome.localId,
              ...(outcome.captureId ? { captureId: outcome.captureId } : {}),
              ...(outcome.message ? { message: outcome.message } : {}),
            }
          : current,
      );
    } finally {
      setWorking(false);
    }
  }

  function dropFrame(fingerprint: string) {
    removeFrame(fingerprint);
    setFrames(listFrames());
  }

  function selectMode(next: CameraModeChoice) {
    if (next === 'burst') {
      navigate(`${jobPath}/capture/rapid`);
      return;
    }
    setMode(next);
  }

  const chipText = [jobName ?? 'Job', workflow ? workflowTitle(workflow) : null].filter(Boolean).join(' · ');

  const preview = (
    <>
      <video ref={videoRef} className="absolute inset-0 size-full object-cover" muted playsInline />
      {streaming && ready && <CropGuide />}
      {!cameraRequested && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted">
          <ImageUp className="size-8" aria-hidden="true" />
          <p className="text-sm">{dragging ? 'Drop to add' : 'Drop a photo'}</p>
        </div>
      )}
      {cameraRequested && !ready && !cameraError && (
        <p className="absolute inset-0 flex items-center justify-center text-sm text-muted">Starting camera…</p>
      )}
      {cameraRequested && cameraError && (
        <p className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-warning">
          {cameraError}
        </p>
      )}

      {/* Top of the preview: Back to the job, the job and purpose (tap to
          change the purpose), and the lens controls. */}
      <div className="absolute inset-x-0 top-0 flex items-start gap-2 p-2.5">
        <OverlayLink to={jobPath} label="Back" icon={ArrowLeft} />
        <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
          <Link
            to={`${jobPath}/capture/workflow`}
            className="flex min-h-11 max-w-full items-center truncate rounded-control border border-border-strong bg-surface/80 px-3 text-xs text-surface-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span className="truncate">{chipText}</span>
          </Link>
          {!online && (
            <span className="rounded-badge border border-warning bg-surface/80 px-2 py-0.5 text-xs text-warning">
              Offline
            </span>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          {canFlip && <OverlayButton label="Switch camera" icon={SwitchCamera} onClick={flip} />}
          {!cameraRequested && (
            <OverlayButton label="Use camera" icon={Camera} onClick={() => setCameraRequested(true)} />
          )}
          {zoom && (
            <button
              type="button"
              onClick={() => setZoom(nextZoom(zoom))}
              aria-label={`Zoom ${zoom.value.toFixed(1)}x`}
              className="flex size-11 items-center justify-center rounded-full border border-border-strong bg-surface/80 text-xs tabular-nums text-surface-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {zoom.value.toFixed(1)}x
            </button>
          )}
          <OverlayButton
            label="Barcode"
            icon={ScanBarcode}
            pressed={barcodeEnabled}
            onClick={() => setBarcodeEnabled((value) => !value)}
          />
        </div>
      </div>

      {/* Bottom of the preview: refusals, the shot list slots, Mic and Tips. */}
      {!shot && (
        <div className="absolute inset-x-0 bottom-0 flex flex-col items-start gap-2 p-2.5">
          {status && (
            <p role="status" className="rounded-control border border-warning bg-surface/90 px-3 py-1.5 text-xs text-warning">
              {status}
            </p>
          )}
          <div className="flex w-full items-end gap-2">
            <ul aria-label="Shot list" className="flex min-w-0 items-end gap-2">
              {slots.map(({ label, frame }, index) => (
                <li key={frame?.fingerprint ?? `slot-${index}`} className="relative">
                  {frame ? (
                    <div
                      className={cn(
                        'relative size-14 overflow-hidden rounded-control border-2 bg-surface',
                        GRADE_RING[frame.advice.grade],
                      )}
                    >
                      {frame.contentType === DOCUMENT_CONTENT_TYPE ? (
                        <FileText className="m-auto mt-3 size-5 text-muted" aria-hidden="true" />
                      ) : (
                        <img
                          src={thumbnails[frame.fingerprint]}
                          alt={label}
                          className="size-full object-cover"
                        />
                      )}
                      <span className="absolute inset-x-0 bottom-0 line-clamp-2 break-words bg-surface/80 px-0.5 py-px text-center text-[10px] leading-3 text-surface-foreground">
                        {label}
                      </span>
                      {/* The control sits inside the slot rather than on its
                          corner: a 44px target hung outside a 56px slot reaches
                          over the one beside it. */}
                      <button
                        type="button"
                        onClick={() => dropFrame(frame.fingerprint)}
                        aria-label={`Remove ${label}`}
                        className="absolute right-0 top-0 flex size-11 items-start justify-end focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                      >
                        <span className="m-0.5 flex size-5 items-center justify-center rounded-full border border-border-strong bg-surface/90 text-muted">
                          <X className="size-3" aria-hidden="true" />
                        </span>
                      </button>
                    </div>
                  ) : (
                    <div
                      data-slot-empty=""
                      className="flex size-14 items-end justify-center rounded-control border border-dashed border-border-strong bg-surface/60 px-0.5 pb-1 text-center text-[10px] leading-3 text-surface-foreground"
                    >
                      {label}
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <span className="flex-1" />
            <OverlayButton
              label={note ? 'Voice note saved' : 'Voice note'}
              icon={Mic}
              marked={note !== null}
              onClick={() => setVoiceOpen(true)}
            />
            <OverlayButton label="Tips" icon={CircleHelp} onClick={tips.show} />
          </div>
        </div>
      )}

      {shot && (
        <div className="absolute inset-0 flex flex-col bg-surface">
          {shot.contentType === DOCUMENT_CONTENT_TYPE ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 text-muted">
              <FileText className="size-10" aria-hidden="true" />
              <p className="text-xs">PDF</p>
            </div>
          ) : shot.url ? (
            <img src={shot.url} alt="Sent frame" className="min-h-0 flex-1 object-contain" />
          ) : (
            <div className="min-h-0 flex-1" />
          )}
          <div className="border-t border-border-strong p-3">
            <p
              className={cn(
                'text-sm font-medium',
                shot.delivery === 'refused' ? 'text-danger' : 'text-surface-foreground',
              )}
              role={shot.delivery === 'refused' ? 'alert' : undefined}
            >
              {deliveryLine(shot, online)}
            </p>
            {shot.advice && (
              <>
                <p className={cn('mt-2 text-xs font-medium', GRADE_TONE[shot.advice.grade])}>
                  {gradeLabel(shot.advice.grade)}
                </p>
                <ul className="mt-1 flex flex-col gap-1">
                  {shot.advice.suggestions.map((suggestion) => (
                    <li key={suggestion} className="text-xs text-muted">
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {/* An arrived capture is worth checking while the operator can
                  still pick the item up; one that has not arrived points at
                  the queue. */}
              {shot.delivery === 'refused' ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    void removeQueuedCapture(shot.localId).then(() => setShot(null));
                  }}
                >
                  Discard
                </Button>
              ) : shot.captureId ? (
                <Button asChild variant="outline">
                  <Link to={`/capture/${shot.captureId}/result`}>View result</Link>
                </Button>
              ) : (
                <Button asChild variant="outline">
                  <Link to="/queue">Queue</Link>
                </Button>
              )}
              <Button onClick={() => setShot(null)}>Next item</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      <CameraLayout
        preview={preview}
        highlighted={dragging}
        previewProps={
          cameraRequested
            ? undefined
            : {
                onDragOver: (event) => {
                  event.preventDefault();
                  setDragging(true);
                },
                onDragLeave: () => setDragging(false),
                onDrop: (event) => void handleDrop(event),
              }
        }
      >
        <ModeSwitch active={mode} onSelect={selectMode} />
        <div className="flex items-center justify-between gap-4 sm:justify-start">
          <SideControl
            label="Image"
            icon={ImageUp}
            disabled={working}
            onClick={() => fileInputRef.current?.click()}
          />
          <ShutterButton
            label={working ? 'Processing' : shutterLabel}
            disabled={working || (streaming && !ready)}
            onClick={pressShutter}
          />
          <SideControl
            label={frames.length > 1 ? `Send ${frames.length}` : 'Send'}
            icon={SendHorizontal}
            primary={frames.length > 0}
            disabled={frames.length === 0 || working}
            onClick={() => void queueFrames()}
          />
        </div>
      </CameraLayout>

      {/*
        `accept` names no HEIC or HEIF on purpose. iOS Safari transcodes a
        chosen photo to JPEG for a file input, and hands over the untouched
        HEIC as soon as the input says it takes one.

        The native camera input gives the full lens and zoom range a browser
        stream does not reach (CAPTURE_JOBS.md section 6); the shutter opens
        it when the live stream is unavailable on a touch device.
      */}
      <input
        ref={phoneCameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => void handleFileChosen(event)}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={mode === 'document' ? `image/*,${DOCUMENT_CONTENT_TYPE}` : 'image/*'}
        className="sr-only"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => void handleFileChosen(event)}
      />

      {/* The voice note for the capture being built (CAPTURE_JOBS.md
          sections 3 and 6), in a sheet so the keyboard and the transcript
          never push the shutter off screen. */}
      <Sheet open={voiceOpen} onClose={() => setVoiceOpen(false)} title="Voice note">
        {note ? (
          <div className="flex flex-col gap-3">
            <p className="whitespace-pre-line rounded-control border border-border p-3 text-sm">{note.transcript}</p>
            <Button variant="outline" onClick={() => holdNote(null)}>
              Remove
            </Button>
          </div>
        ) : (
          <LiveTranscriptBox
            onSave={(transcript, durationMs) => {
              holdNote({ transcript, durationMs });
              setVoiceOpen(false);
            }}
          />
        )}
      </Sheet>

      {/* The first-run guide (DECISIONS.md item 4), reopened from Tips. */}
      <Sheet open={tips.open} onClose={tips.close} title="Tips">
        <ul className="flex flex-col gap-3">
          {CAPTURE_TIPS.map((tip, index) => {
            const TipIcon = TIP_ICONS[index] ?? Scan;
            return (
              <li key={tip} className="flex min-h-11 items-center gap-3 text-base">
                <TipIcon className="size-5 shrink-0 text-accent" aria-hidden="true" />
                {tip}
              </li>
            );
          })}
        </ul>
        <Button size="lg" className="mt-4 w-full" onClick={tips.close}>
          Done
        </Button>
      </Sheet>
    </>
  );
}
